import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { BLOG_CATEGORY_FILTERS, BlogCategoryId, BlogPostDto, Notification } from '../types';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { isPlatformSystemAdmin } from '../utils/roleUtils';
import { resolveBlogFeaturedImageSrc } from '../utils/blogImage';
import SeoHead from '../components/SeoHead';
import { BLOG_INDEX_SEO, ORGANIZATION_JSON_LD, buildBlogCollectionJsonLd, publicPath } from '../utils/publicSite';
import './BlogPage.design.css';

type SortMode = 'newest' | 'read-time' | 'title';

const PAGE_SIZE = 25;

const SORT_OPTIONS: Array<{ id: SortMode; label: string }> = [
  { id: 'newest', label: 'Newest first' },
  { id: 'read-time', label: 'Shortest read' },
  { id: 'title', label: 'Title A to Z' },
];

const BLOG_LIST_FILTERS_KEY = 'aacore.blog.listFilters';
const SORT_IDS = new Set<SortMode>(SORT_OPTIONS.map((option) => option.id));

type BlogListFilters = {
  q: string;
  category: BlogCategoryId | 'all';
  sort: SortMode;
};

const DEFAULT_BLOG_FILTERS: BlogListFilters = { q: '', category: 'all', sort: 'newest' };

function parseSortMode(value: string | null): SortMode {
  return value && SORT_IDS.has(value as SortMode) ? (value as SortMode) : 'newest';
}

function parseCategoryFilter(value: string | null): BlogCategoryId | 'all' {
  if (!value || value === 'all') return 'all';
  return BLOG_CATEGORY_FILTERS.some((category) => category.id === value) ? (value as BlogCategoryId) : 'all';
}

function isDefaultBlogFilters(filters: BlogListFilters): boolean {
  return filters.q.trim() === '' && filters.category === 'all' && filters.sort === 'newest';
}

function filtersFromSearchParams(params: URLSearchParams): BlogListFilters | null {
  if (!params.has('q') && !params.has('category') && !params.has('sort')) return null;
  return {
    q: params.get('q') ?? '',
    category: parseCategoryFilter(params.get('category')),
    sort: parseSortMode(params.get('sort')),
  };
}

function readStoredBlogFilters(): BlogListFilters | null {
  try {
    const raw = sessionStorage.getItem(BLOG_LIST_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BlogListFilters>;
    return {
      q: typeof parsed.q === 'string' ? parsed.q : '',
      category: parseCategoryFilter(typeof parsed.category === 'string' ? parsed.category : null),
      sort: parseSortMode(typeof parsed.sort === 'string' ? parsed.sort : null),
    };
  } catch {
    return null;
  }
}

function storeBlogFilters(filters: BlogListFilters) {
  try {
    sessionStorage.setItem(BLOG_LIST_FILTERS_KEY, JSON.stringify({
      q: filters.q.trim(),
      category: filters.category,
      sort: filters.sort,
    }));
  } catch {
    // Ignore private-mode and quota failures. The URL still holds the current filters.
  }
}

function blogFilterSearchParams(filters: BlogListFilters, pageNum: number): URLSearchParams {
  const next = new URLSearchParams();
  const q = filters.q.trim();
  if (q) next.set('q', q);
  if (filters.category !== 'all') next.set('category', filters.category);
  if (filters.sort !== 'newest') next.set('sort', filters.sort);
  if (pageNum > 1) next.set('page', String(pageNum));
  return next;
}

const CATEGORY_LABELS: Record<string, string> = {
  'getting-started': 'Getting Started',
  instructions: 'Instructions',
  'release-notes': 'Release Notes',
  compliance: 'Compliance',
  'founder-updates': 'Founder Updates',
};

const CATEGORY_IMAGE_CLASS: Record<string, string> = {
  'getting-started': 'image-start',
  instructions: 'image-setup',
  'release-notes': 'image-updates',
  compliance: 'image-payments',
  'founder-updates': 'image-customer',
};

const SECTION_IDS = ['overview', 'articles', 'get-started'] as const;

function formatBlogDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function postImageUrl(post: BlogPostDto): string | null {
  return resolveBlogFeaturedImageSrc(post);
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const ArrowIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const SelectCaretIcon = () => (
  <svg className="select-caret" aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const TopicLeadingIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    <circle cx="18.5" cy="16.5" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const SortLeadingIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path d="M7 7h10M7 12h7M7 17h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    <path d="m17 15 2 2 2-2M19 17V7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
  </svg>
);

function SortOptionIcon({ id }: { id: SortMode }) {
  switch (id) {
    case 'newest':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M12 7v10m0 0-3.5-3.5M12 17l3.5-3.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
          <path d="M5 5h14" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      );
    case 'read-time':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 8v4.5l2.5 1.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        </svg>
      );
    case 'title':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M5 7h9M9.5 7v12M15 11h4M17 11v8" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      );
    default:
      return null;
  }
}

function TopicOptionIcon({ id }: { id: BlogCategoryId | 'all' }) {
  switch (id) {
    case 'all':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M4 6h6v6H4V6Zm10 0h6v6h-6V6ZM4 16h6v4H4v-4Zm10-2h6v6h-6v-6Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        </svg>
      );
    case 'getting-started':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M5 19V5l14 7-14 7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case 'instructions':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M7 4h10a2 2 0 0 1 2 2v14l-7-3-7 3V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M9 9h6M9 13h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      );
    case 'release-notes':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M12 3v3M12 18v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M3 12h3M18 12h3M4.9 19.1 7 17M17 7l2.1-2.1" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
          <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'compliance':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <path d="M12 3 5 6v6c0 4.2 2.9 7.3 7 8.5 4.1-1.2 7-4.3 7-8.5V6l-7-3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="m9.2 12 1.9 1.9 3.7-3.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        </svg>
      );
    case 'founder-updates':
      return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M5.5 19.5c1.6-3 3.8-4.5 6.5-4.5s4.9 1.5 6.5 4.5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
        </svg>
      );
    default:
      return null;
  }
}

const BlogPage: React.FC = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [posts, setPosts] = useState<BlogPostDto[]>([]);
  const [featured, setFeatured] = useState<BlogPostDto | null>(null);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: PAGE_SIZE,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = filtersFromSearchParams(searchParams);
  const allowStoredFiltersRef = React.useRef(urlFilters == null);
  if (urlFilters) allowStoredFiltersRef.current = false;
  const storedFilters = !urlFilters && allowStoredFiltersRef.current ? readStoredBlogFilters() : null;
  const categoryFilter = urlFilters?.category ?? storedFilters?.category ?? DEFAULT_BLOG_FILTERS.category;
  const sortMode = urlFilters?.sort ?? storedFilters?.sort ?? DEFAULT_BLOG_FILTERS.sort;
  const initialQuery = urlFilters?.q ?? storedFilters?.q ?? '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [debouncedQ, setDebouncedQ] = useState(initialQuery.trim());
  const pendingQRef = React.useRef(initialQuery.trim());
  const initialQueryRef = React.useRef(initialQuery.trim());
  const hydratedRef = React.useRef(false);
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
  const [topicMenuOpen, setTopicMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [jumpMenuOpen, setJumpMenuOpen] = useState(false);

  const canAuthor = Boolean(user && isPlatformSystemAdmin(user));

  const applyBlogFilters = (
    filters: BlogListFilters,
    options?: { replace?: boolean; page?: number }
  ) => {
    const q = filters.q.trim();
    pendingQRef.current = q;
    const nextFilters = { ...filters, q };
    storeBlogFilters(nextFilters);
    setSearchParams(blogFilterSearchParams(nextFilters, options?.page ?? 1), { replace: options?.replace ?? false });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        setUnreadNotifications(notifications.filter((n: Notification) => !n.is_read));
      } catch {
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);

  useEffect(() => {
    const fromUrl = filtersFromSearchParams(searchParams);
    if (fromUrl) {
      hydratedRef.current = true;
      allowStoredFiltersRef.current = false;
      storeBlogFilters(fromUrl);
      const urlQ = fromUrl.q.trim();
      if (urlQ !== pendingQRef.current) {
        pendingQRef.current = urlQ;
        setSearchQuery(fromUrl.q);
        setDebouncedQ(urlQ);
      }
      return;
    }

    if (!hydratedRef.current) {
      hydratedRef.current = true;
      if (pendingQRef.current !== initialQueryRef.current) return;
      const stored = readStoredBlogFilters();
      if (!stored || isDefaultBlogFilters(stored)) return;
      const q = stored.q.trim();
      const pageNum = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
      pendingQRef.current = q;
      setSearchQuery(stored.q);
      setDebouncedQ(q);
      storeBlogFilters({ ...stored, q });
      setSearchParams(blogFilterSearchParams({ ...stored, q }, pageNum), { replace: true });
      return;
    }

    storeBlogFilters(DEFAULT_BLOG_FILTERS);
    if (pendingQRef.current !== '') {
      pendingQRef.current = '';
      setSearchQuery('');
      setDebouncedQ('');
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const latestFiltersRef = React.useRef({ q: searchQuery, category: categoryFilter, sort: sortMode });
  latestFiltersRef.current = { q: searchQuery, category: categoryFilter, sort: sortMode };

  useEffect(() => {
    return () => {
      const latest = latestFiltersRef.current;
      storeBlogFilters({
        q: latest.q.trim(),
        category: latest.category,
        sort: latest.sort,
      });
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const result = await apiService.getBlogPosts({
          category: categoryFilter === 'all' ? undefined : categoryFilter,
          q: debouncedQ || undefined,
          sort: sortMode,
          page,
          limit: PAGE_SIZE,
        });
        if (!cancelled) {
          setPosts(result.posts);
          setPagination(result.pagination);
          setCategoryCounts(result.categoryCounts as Record<string, number>);
          setFeatured(result.featured);
          setLoadError(null);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load blog posts');
          setPosts([]);
          setFeatured(null);
          setCategoryCounts({});
          setPagination((p) => ({ ...p, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, debouncedQ, categoryFilter, sortMode]);

  useEffect(() => {
    if (!jumpMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.section-jumper')) setJumpMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [jumpMenuOpen]);

  useEffect(() => {
    if (!topicMenuOpen && !sortMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.topic-control')) setTopicMenuOpen(false);
      if (!target?.closest('.sort-control')) setSortMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setTopicMenuOpen(false);
        setSortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [topicMenuOpen, sortMenuOpen]);

  const secondaryFeatured = useMemo(() => {
    const candidates = featured ? posts.filter((p) => p.slug !== featured.slug) : posts;
    if (!candidates.length) return null;
    return candidates.find((p) => p.category === 'getting-started') ?? candidates[0] ?? null;
  }, [featured, posts]);

  const listPosts = useMemo(() => {
    const exclude = new Set<string>();
    if (featured?.slug) exclude.add(featured.slug);
    if (secondaryFeatured?.slug) exclude.add(secondaryFeatured.slug);
    return posts.filter((p) => !exclude.has(p.slug));
  }, [posts, featured, secondaryFeatured]);

  const rangeStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const rangeEnd = Math.min(pagination.page * pagination.limit, pagination.total);
  const statPublished = categoryCounts.all ?? pagination.total ?? 0;
  const topicCount = BLOG_CATEGORY_FILTERS.filter((c) => c.id !== 'all').length;
  const selectedTopicLabel =
    categoryFilter === 'all'
      ? 'All topics'
      : BLOG_CATEGORY_FILTERS.find((c) => c.id === categoryFilter)?.label ?? 'All topics';
  const selectedSortLabel = SORT_OPTIONS.find((o) => o.id === sortMode)?.label ?? 'Newest first';
  const blogFiltered = Boolean(debouncedQ) || categoryFilter !== 'all' || sortMode !== 'newest';
  const blogCanonicalPath = !blogFiltered && page > 1 ? `/blog?page=${page}` : BLOG_INDEX_SEO.path;
  const visibleBlogPosts = posts.map((post) => ({ slug: post.slug, title: post.title }));
  const blogJsonLd =
    visibleBlogPosts.length > 0
      ? [ORGANIZATION_JSON_LD, buildBlogCollectionJsonLd(visibleBlogPosts)]
      : ORGANIZATION_JSON_LD;
  const articlePath = (slug: string) => publicPath(`/blog/${slug}`);
  const blogPageHref = (nextPage: number) => {
    const params = blogFilterSearchParams(
      { q: searchQuery, category: categoryFilter, sort: sortMode },
      nextPage
    );
    const qs = params.toString();
    return publicPath(qs ? `/blog?${qs}` : '/blog');
  };

  const jumpToAdjacentSection = (direction: -1 | 1) => {
    const current =
      SECTION_IDS.find((id) => {
        const el = document.getElementById(id);
        if (!el) return false;
        const top = el.getBoundingClientRect().top;
        return top >= -40 && top < window.innerHeight * 0.45;
      }) ?? SECTION_IDS[0];
    const idx = SECTION_IDS.indexOf(current);
    const next = SECTION_IDS[Math.max(0, Math.min(SECTION_IDS.length - 1, idx + direction))];
    scrollToSection(next);
    setJumpMenuOpen(false);
  };

  const renderThumb = (post: BlogPostDto, className: string, label: string) => {
    const imageUrl = postImageUrl(post);
    const tone = CATEGORY_IMAGE_CLASS[post.category] || 'image-start';
    return (
      <div
        className={`post-featured-image ${className} ${imageUrl ? 'has-photo' : tone}`}
        aria-label={`${post.title} featured image`}
      >
        {imageUrl ? <img src={imageUrl} alt="" /> : null}
        <span>{label}</span>
      </div>
    );
  };

  const renderListCard = (post: BlogPostDto) => {
    const mins = post.readTimeMinutes ?? 1;
    const categoryLabel = CATEGORY_LABELS[post.category] || post.category;
    const tags = (post.tags || []).slice(0, 3);

    return (
      <article key={post.slug} className="older-post-card">
        {renderThumb(post, 'list-thumb', post.badge || categoryLabel)}
        <span className="post-category">{categoryLabel}</span>
        <h3>
          <Link to={articlePath(post.slug)}>{post.title}</Link>
        </h3>
        <p>{post.summary}</p>
        {tags.length > 0 && (
          <div className="post-tags" aria-label="Article topics">
            {tags.map((tag) => (
              <span key={`${post.slug}-${tag.label}`}>{tag.label}</span>
            ))}
          </div>
        )}
        <div className="post-meta">
          <span>{post.authorDisplayName || 'AACore Booking'}</span>
          {post.publishedAt ? <time dateTime={post.publishedAt}>{formatBlogDate(post.publishedAt)}</time> : null}
          <span>{mins} min read</span>
        </div>
        <div className="article-link-row">
          <Link to={articlePath(post.slug)}>
            Read article <ArrowIcon />
          </Link>
          {canAuthor && (
            <Link to={`/blog/edit/${post.slug}`} className="text-xs font-medium opacity-80 hover:opacity-100">
              Edit
            </Link>
          )}
        </div>
      </article>
    );
  };

  const renderFeaturedCard = (post: BlogPostDto, cta = 'Read guide') => {
    const mins = post.readTimeMinutes ?? 1;
    const categoryLabel = CATEGORY_LABELS[post.category] || post.category;
    return (
      <article key={post.slug} className="featured-post-card">
        <span className="post-category">{categoryLabel}</span>
        <h3>
          <Link to={articlePath(post.slug)}>{post.title}</Link>
        </h3>
        {renderThumb(post, 'featured-thumb', post.badge || categoryLabel)}
        <p>{post.summary}</p>
        <div className="post-meta">
          <span>{post.authorDisplayName || 'AACore Booking'}</span>
          {post.publishedAt ? <time dateTime={post.publishedAt}>{formatBlogDate(post.publishedAt)}</time> : null}
          <span>{mins} min read</span>
        </div>
        <div className="article-link-row">
          <Link to={articlePath(post.slug)}>
            {cta} <ArrowIcon />
          </Link>
        </div>
      </article>
    );
  };

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f8fc]">
        <SeoHead
          title={BLOG_INDEX_SEO.title}
          description={BLOG_INDEX_SEO.description}
          canonicalPath={blogCanonicalPath}
          robots={blogFiltered ? 'noindex, follow' : undefined}
          jsonLd={blogJsonLd}
        />
        <CustomerNavbar
          onLogout={handleLogout}
          showDashboardLink
          showLogoutButton
          showAuthButtons
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex items-center justify-center py-24">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f8fc]">
      <SeoHead
        title={BLOG_INDEX_SEO.title}
        description={BLOG_INDEX_SEO.description}
        canonicalPath={blogCanonicalPath}
        robots={blogFiltered ? 'noindex, follow' : undefined}
        jsonLd={blogJsonLd}
      />
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink
        showLogoutButton
        showAuthButtons
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <div className="aacore-blog">
        <aside className="section-jumper" aria-label="Quick section navigation">
          <button type="button" className="jump-prev" aria-label="Previous section" onClick={() => jumpToAdjacentSection(-1)}>
            <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
              <path d="m7 14 5-5 5 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
          </button>
          <button
            type="button"
            className="jump-menu-toggle"
            aria-label="Open section list"
            aria-expanded={jumpMenuOpen}
            aria-controls="sectionJumpMenu"
            onClick={() => setJumpMenuOpen((o) => !o)}
          >
            <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
              <path d="M5 7h14M5 12h14M5 17h14" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
            </svg>
          </button>
          <div className="section-jump-menu" id="sectionJumpMenu" data-open={jumpMenuOpen ? 'true' : 'false'}>
            <button
              type="button"
              onClick={() => {
                scrollToSection('overview');
                setJumpMenuOpen(false);
              }}
            >
              <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                <path d="M3.5 10.8 12 4l8.5 6.8v8.4a1.8 1.8 0 0 1-1.8 1.8H5.3a1.8 1.8 0 0 1-1.8-1.8v-8.4Z" stroke="currentColor" strokeWidth="1.8" />
                <path d="M9 21v-7h6v7" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              Overview
            </button>
            <button
              type="button"
              onClick={() => {
                scrollToSection('articles');
                setJumpMenuOpen(false);
              }}
            >
              <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                <path d="M5 4.5h14v15H5v-15Z" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
              </svg>
              Articles
            </button>
            <button
              type="button"
              onClick={() => {
                scrollToSection('get-started');
                setJumpMenuOpen(false);
              }}
            >
              <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
              </svg>
              Get started
            </button>
          </div>
          <button type="button" className="jump-next" aria-label="Next section" onClick={() => jumpToAdjacentSection(1)}>
            <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
              <path d="m7 10 5 5 5-5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
          </button>
        </aside>

        <main id="main">
          <section className="hero" id="overview" aria-labelledby="hero-title">
            <div className="wrap hero-grid">
              <div className="hero-copy">
                <p className="eyebrow">AACore Booking Blog</p>
                <h1 id="hero-title">
                  Helpful guidance for <span>service businesses.</span>
                </h1>
                <p className="hero-lead">
                  Practical articles for setting up services, accepting appointments, managing customers, sending invoices,
                  collecting payments, and getting more value from AACore Booking.
                </p>

                <div className="hero-points" aria-label="Resource highlights">
                  <div className="hero-point">
                    <span className="micro-icon">
                      <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                        <path d="M5 5h14v14H5V5Z" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                      </svg>
                    </span>
                    <div>
                      <strong>Get started</strong>
                      <small>Prepare services, hours, and policies.</small>
                    </div>
                  </div>
                  <div className="hero-point">
                    <span className="micro-icon">
                      <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                        <path d="M7 3.5v3M17 3.5v3M4.5 8h15M5 5.5h14v14H5v-14Z" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                        <path d="M8 12h3M13 12h3M8 16h3" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                      </svg>
                    </span>
                    <div>
                      <strong>Manage bookings</strong>
                      <small>Improve the customer appointment journey.</small>
                    </div>
                  </div>
                  <div className="hero-point">
                    <span className="micro-icon">
                      <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                        <path d="M4 7h16v10H4V7Z" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M4 10h16M8 14h3" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                      </svg>
                    </span>
                    <div>
                      <strong>Invoice and get paid</strong>
                      <small>Learn payment and invoice workflows.</small>
                    </div>
                  </div>
                </div>

                <div className="hero-actions" aria-label="Blog page actions">
                  <button type="button" className="button primary" onClick={() => scrollToSection('articles')}>
                    Browse articles
                    <ArrowIcon />
                  </button>
                  {featured ? (
                    <Link className="button secondary" to={articlePath(featured.slug)}>
                      <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                        <path d="M5 4.5h14v15H5v-15Z" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                      </svg>
                      Start with the overview
                    </Link>
                  ) : (
                    <button type="button" className="button secondary" onClick={() => scrollToSection('articles')}>
                      <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                        <path d="M5 4.5h14v15H5v-15Z" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                      </svg>
                      Browse topics
                    </button>
                  )}
                </div>

                {canAuthor && (
                  <div className="author-actions">
                    <Link to="/blog/new" className="button secondary">
                      Create New Post
                    </Link>
                  </div>
                )}
              </div>

              <div className="hero-panel" aria-label="AACore Booking resource hub preview">
                <div className="mock-app">
                  <div className="mock-topbar">
                    <div className="mock-dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="mock-title">Resource Hub</div>
                  </div>
                  <div className="mock-content">
                    <div className="metric-row">
                      <div className="metric-card">
                        <strong>{statPublished}</strong>
                        <span>Helpful articles</span>
                      </div>
                      <div className="metric-card">
                        <strong>{featured ? 1 + (secondaryFeatured ? 1 : 0) : Math.min(2, listPosts.length)}</strong>
                        <span>Featured guides</span>
                      </div>
                      <div className="metric-card">
                        <strong>{topicCount}</strong>
                        <span>Core topics</span>
                      </div>
                    </div>
                    <ul className="preview-list">
                      <li>
                        <span className="micro-icon">
                          <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                            <path d="M12 3.5 20 8v8l-8 4.5L4 16V8l8-4.5Z" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M4.5 8.3 12 12.5l7.5-4.2M12 12.5v8" stroke="currentColor" strokeWidth="1.8" />
                          </svg>
                        </span>
                        <div>
                          <strong>Launch your booking site</strong>
                          <small>Build the essentials before going public.</small>
                        </div>
                        <span className="preview-status">Start</span>
                      </li>
                      <li>
                        <span className="micro-icon">
                          <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                            <path d="M5 6h14v12H5V6Z" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M8 10h8M8 14h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                          </svg>
                        </span>
                        <div>
                          <strong>Keep booking simple</strong>
                          <small>Reduce friction from service selection to confirmation.</small>
                        </div>
                        <span className="preview-status">Setup</span>
                      </li>
                      <li>
                        <span className="micro-icon">
                          <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                            <path d="M5 4h14v16H5V4Z" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M8 8h8M8 12h8M8 16h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                          </svg>
                        </span>
                        <div>
                          <strong>Use invoices and payments</strong>
                          <small>Help customers review and pay with confidence.</small>
                        </div>
                        <span className="preview-status">Payments</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="blog-list-section" id="articles" aria-labelledby="articles-title" aria-busy={loading}>
            <div className="wrap article-hub-layout">
              <div className="article-main">
                <div className="section-head">
                  <p className="eyebrow">Articles</p>
                  <h2 className="section-title" id="articles-title">
                    Practical resources for everyday booking work.
                  </h2>
                  <p className="section-text">
                    Search by topic or browse the latest guidance for business setup, customer appointments, invoices,
                    payments, and product updates.
                  </p>
                </div>

                {loadError && <div className="load-error">{loadError}</div>}

                <div className="blog-toolbar" aria-label="Blog filters">
                  <label className="search-box">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="m16.2 16.2 3.8 3.8" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                    </svg>
                    <input
                      id="blogSearch"
                      type="search"
                      autoComplete="off"
                      placeholder="Search articles, features, or keywords"
                      value={searchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        const q = value.trim();
                        pendingQRef.current = q;
                        setSearchQuery(value);
                        storeBlogFilters({ q, category: categoryFilter, sort: sortMode });
                        setSearchParams(
                          blogFilterSearchParams({ q: value, category: categoryFilter, sort: sortMode }, 1),
                          { replace: true }
                        );
                      }}
                    />
                  </label>

                  <div className="topic-control">
                    <button
                      type="button"
                      className="topic-control-trigger"
                      id="blogCategory"
                      aria-label="Filter articles by category"
                      aria-haspopup="listbox"
                      aria-expanded={topicMenuOpen}
                      aria-controls="blogTopicMenu"
                      onClick={() => {
                        setTopicMenuOpen((o) => !o);
                        setSortMenuOpen(false);
                      }}
                    >
                      <TopicLeadingIcon />
                      <span className="topic-label">{selectedTopicLabel}</span>
                      <SelectCaretIcon />
                    </button>
                    <div
                      className="topic-menu"
                      id="blogTopicMenu"
                      role="listbox"
                      aria-label="Article topics"
                      hidden={!topicMenuOpen}
                    >
                      {BLOG_CATEGORY_FILTERS.map(({ id, label }) => {
                        const optionLabel = id === 'all' ? 'All topics' : label;
                        return (
                          <button
                            key={id}
                            type="button"
                            className="topic-option"
                            role="option"
                            aria-selected={categoryFilter === id}
                            onClick={() => {
                              applyBlogFilters({ q: searchQuery, category: id, sort: sortMode });
                              setDebouncedQ(searchQuery.trim());
                              setTopicMenuOpen(false);
                            }}
                          >
                            <TopicOptionIcon id={id} />
                            <span>{optionLabel}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="sort-control">
                    <button
                      type="button"
                      className="sort-control-trigger"
                      id="blogSort"
                      aria-label="Sort articles"
                      aria-haspopup="listbox"
                      aria-expanded={sortMenuOpen}
                      aria-controls="blogSortMenu"
                      onClick={() => {
                        setSortMenuOpen((o) => !o);
                        setTopicMenuOpen(false);
                      }}
                    >
                      <SortLeadingIcon />
                      <span className="sort-label">{selectedSortLabel}</span>
                      <SelectCaretIcon />
                    </button>
                    <div
                      className="sort-menu"
                      id="blogSortMenu"
                      role="listbox"
                      aria-label="Sort articles"
                      hidden={!sortMenuOpen}
                    >
                      {SORT_OPTIONS.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          className="sort-option"
                          role="option"
                          aria-selected={sortMode === id}
                          onClick={() => {
                            applyBlogFilters({ q: searchQuery, category: categoryFilter, sort: id });
                            setDebouncedQ(searchQuery.trim());
                            setSortMenuOpen(false);
                          }}
                        >
                          <SortOptionIcon id={id} />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <p className="result-count">
                  {pagination.total === 0
                    ? 'No articles to show'
                    : `Showing ${rangeStart}–${rangeEnd} of ${pagination.total} ${pagination.total === 1 ? 'article' : 'articles'}`}
                </p>

                {listPosts.length > 0 ? (
                  <div className="older-post-list">{listPosts.map(renderListCard)}</div>
                ) : (
                  !loadError && (
                    <div className="empty-state">
                      <strong>No articles matched your search.</strong>
                      <span>Try a broader keyword or select all topics.</span>
                    </div>
                  )
                )}

                {pagination.totalPages > 1 && (
                  <nav className="blog-pagination" aria-label="Article pagination">
                    {pagination.hasPrevPage ? (
                      <Link className="pagination-button" to={blogPageHref(page - 1)}>
                        Previous
                      </Link>
                    ) : (
                      <button className="pagination-button" type="button" disabled>
                        Previous
                      </button>
                    )}
                    <div className="pagination-pages" aria-label="Page numbers">
                      Page {pagination.page} of {pagination.totalPages}
                    </div>
                    {pagination.hasNextPage ? (
                      <Link className="pagination-button" to={blogPageHref(page + 1)}>
                        Next
                      </Link>
                    ) : (
                      <button className="pagination-button" type="button" disabled>
                        Next
                      </button>
                    )}
                  </nav>
                )}
              </div>

              <aside className="featured-rail" aria-label="Featured guides">
                <div className="featured-rail-head">
                  <p className="eyebrow">Featured guides</p>
                </div>
                {featured ? renderFeaturedCard(featured) : null}
                {secondaryFeatured ? renderFeaturedCard(secondaryFeatured) : null}
                {!featured && !secondaryFeatured && (
                  <div className="featured-post-card">
                    <p className="section-text" style={{ marginTop: 0 }}>
                      Featured guides will appear here once posts are published.
                    </p>
                  </div>
                )}
              </aside>
            </div>
          </section>

          <section className="final-cta" id="get-started" aria-labelledby="cta-title">
            <div className="wrap">
              <div className="cta-card">
                <div>
                  <h2 id="cta-title">Ready to put the guidance into practice?</h2>
                  <p>
                    Create your AACore Booking business account, prepare your services, and give customers a simple way to
                    book with you online.
                  </p>
                </div>
                <div className="cta-actions">
                  <Link className="button light" to="/features/">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M5 5h14v14H5V5Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                    </svg>
                    Explore features
                  </Link>
                  <Link className="button primary" to="/business-registration/">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
                    </svg>
                    Business sign up
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="site-footer" aria-label="Blog resource links">
            <div className="wrap footer-grid">
              <div className="footer-brand">
                <Link className="brand" to="/" aria-label="AACore Booking home">
                  <span className="brand-mark">B</span>
                  <span className="brand-text">
                    <strong>AACore Booking</strong>
                    <span>Powered by AACore</span>
                  </span>
                </Link>
                <p>
                  A practical platform for service businesses to present services, accept appointments, manage customers,
                  send invoices, and collect payments.
                </p>
              </div>

              <div className="footer-panel">
                <div className="footer-panel-title">
                  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                    <path d="M5 5h14v14H5V5Z" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                  </svg>
                  Business tools
                </div>
                <div className="footer-content">
                  <span className="footer-link">Hosted booking website</span>
                  <span className="footer-link">Services and schedules</span>
                  <span className="footer-link">Invoices and payments</span>
                  <span className="footer-link">Customer reminders</span>
                </div>
              </div>

              <div className="footer-panel">
                <div className="footer-panel-title">
                  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                    <path d="M5 4.5h14v15H5v-15Z" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                  </svg>
                  Helpful links
                </div>
                <div className="footer-content">
                  <Link className="footer-link" to="/features/">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M5 5h14v14H5V5Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                    </svg>
                    Features
                  </Link>
                  <Link className="footer-link" to="/business-registration/">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeLinecap="round" strokeWidth="1.9" />
                    </svg>
                    Business sign up
                  </Link>
                  <Link className="footer-link" to="/privacy">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M12 21s7-3.7 7-10V5.7L12 3 5 5.7V11c0 6.3 7 10 7 10Z" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                    Privacy Policy
                  </Link>
                  <Link className="footer-link" to="/agreements/customer">
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <path d="M6 3.5h9l3 3V20H6V3.5Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M15 3.5V7h3M9 11h6M9 15h6" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
                    </svg>
                    Terms
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
